import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Stack,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDispatch, useSelector } from 'react-redux';
import { createExpense, updateExpense } from '../../apis/expenseSlice';
import { toast } from 'react-toastify';

const ExpenseDialog = ({ open, handleClose, editData, callApi }) => {
  const dispatch = useDispatch();
  const { expenseLoading } = useSelector((state) => state.expenseData);

  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    category: 'other',
    expenseDate: new Date().toISOString().split('T')[0],
    paymentMode: 'cash',
  });

  useEffect(() => {
    if (open) {
      if (editData && editData._id) {
        setFormData({
          description: editData.description,
          amount: editData.amount,
          category: editData.category,
          expenseDate: editData.expenseDate
            ? new Date(editData.expenseDate).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0],
          paymentMode: editData.paymentMode,
        });
      } else {
        setFormData({
          description: '',
          amount: '',
          category: 'other',
          expenseDate: new Date().toISOString().split('T')[0],
          paymentMode: 'cash',
        });
      }
    }
  }, [editData, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'amount' ? value : value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.description) return toast.error('Description is required');
    if (Number(formData.amount) <= 0) return toast.error('Amount must be greater than 0');

    const payload = {
      description: formData.description,
      amount: Number(formData.amount),
      category: formData.category,
      expenseDate: new Date(formData.expenseDate),
      paymentMode: formData.paymentMode,
    };

    const response =
      editData && editData._id
        ? await dispatch(updateExpense({ id: editData._id, data: payload }))
        : await dispatch(createExpense(payload));

    if (!response.payload?.error) {
      toast.success(response.payload?.message || 'Expense saved successfully');
      handleClose();
      callApi();
    } else {
      toast.error(response.payload?.message || 'Error saving expense');
    }
  };

  const disbaled = !formData.description.trim() || Number(formData.amount) <= 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 2, border: '1px solid var(--color-border-soft)' } }}
    >
      <DialogTitle sx={{ fontWeight: 700, color: 'var(--color-heading)', borderBottom: '1px solid var(--color-border-soft)' }}>
        {editData && editData._id ? 'Edit Expense' : 'Add Expense'}
      </DialogTitle>
      <DialogContent
        sx={{
          pt: '12px !important',
          '& .MuiInputBase-root': { fontSize: '0.84rem' },
          '& .MuiInputLabel-root': { fontSize: '0.8rem' },
        }}
      >
        <Stack spacing={2} sx={{ marginTop: 1 }}>
          <TextField
            label="Description"
            required
            fullWidth
            value={formData.description}
            onChange={handleChange}
            name="description"
            placeholder="e.g., Office supplies"
          />
          <TextField
            label="Amount"
            type="number"
            fullWidth
            value={formData.amount}
            onChange={handleChange}
            name="amount"
            InputProps={{ step: '0.01', min: '0' }}
            required
          />
          <TextField
            label="Category"
            select
            fullWidth
            value={formData.category}
            onChange={handleChange}
            name="category"
          >
            <MenuItem value="utilities">Utilities</MenuItem>
            <MenuItem value="salary">Salary</MenuItem>
            <MenuItem value="rent">Rent</MenuItem>
            <MenuItem value="supplies">Supplies</MenuItem>
            <MenuItem value="maintenance">Maintenance</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
          <TextField
            label="Payment Mode"
            select
            fullWidth
            value={formData.paymentMode}
            onChange={handleChange}
            name="paymentMode"
          >
            <MenuItem value="cash">Cash</MenuItem>
            <MenuItem value="bank">Bank</MenuItem>
            <MenuItem value="check">Check</MenuItem>
            <MenuItem value="upi">Upi</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
          <TextField
            label="Date"
            type="date"
            fullWidth
            value={formData.expenseDate}
            onChange={handleChange}
            name="expenseDate"
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid var(--color-border-soft)', p: 1.25 }}>
        <Button
          onClick={handleClose}
          sx={{ textTransform: 'none', borderRadius: '8px', border: '1px solid var(--color-border-soft)', color: 'var(--color-heading)' }}
        >
          Cancel
        </Button>
        <LoadingButton
          loading={expenseLoading}
          onClick={handleSubmit}
          variant="contained"
          // sx={{
          //   backgroundImage: 'linear-gradient(180deg, #4B45FF 0%, #191C63 100%)',
          // }}
          className={disbaled ? "dialogDisableBtn" : "dialogSubmitBtn"}
          disabled={disbaled}
          sx={{ textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ExpenseDialog;
